import React, { useState } from "react";
import { X, CheckCircle2, Clock, Loader2, CreditCard } from "lucide-react";
import toast from "react-hot-toast";
import { InverntroyService } from "../../../../../services/api/inventroy.service";

interface UpdatePaymentStatusModalProps {
  inwardStockId: string;
  invoiceNumber: string;
  currentStatus: string;
  onClose: () => void;
  onSuccess: (newStatus: string) => void;
}

const UpdatePaymentStatusModal: React.FC<UpdatePaymentStatusModalProps> = ({
  inwardStockId,
  invoiceNumber,
  currentStatus,
  onClose,
  onSuccess,
}) => {
  const normalizedCurrentStatus =
    currentStatus?.toLowerCase() === "completed" ||
    currentStatus?.toLowerCase() === "success"
      ? "completed"
      : "pending";

  const [selectedStatus, setSelectedStatus] = useState<"completed" | "pending">(
    normalizedCurrentStatus
  );
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!inwardStockId) {
      toast.error("Inward stock identifier not found");
      return;
    }

    try {
      setIsLoading(true);
      const res = await InverntroyService.updatePaymentStatus(
        inwardStockId,
        selectedStatus
      );

      if (res && res.success === false) {
        toast.error(res.message || "Failed to update payment status");
        return;
      }

      toast.success(res?.message || "Payment status updated successfully");
      onSuccess(selectedStatus);
      onClose();
    } catch (error) {
      console.error("Error in update payment status:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to update payment status"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-md overflow-hidden transform transition-all">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 text-[#199C4D] flex items-center justify-center border border-emerald-100 shadow-sm">
              <CreditCard className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                Update Payment Status
              </h3>
              <p className="text-xs text-gray-500">
                Invoice: <span className="font-medium text-gray-700">{invoiceNumber}</span>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Current Status Badge */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-200/60 text-xs">
            <span className="text-gray-500 font-medium">Current Status</span>
            <span
              className={`px-2.5 py-1 rounded-full font-semibold capitalize ${
                normalizedCurrentStatus === "completed"
                  ? "bg-green-100 text-green-800"
                  : "bg-amber-100 text-amber-800"
              }`}
            >
              {currentStatus || "Pending"}
            </span>
          </div>

          {/* Select Status Options */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2.5">
              Select New Payment Status
            </label>
            <div className="space-y-3">
              {/* Completed Option */}
              <div
                onClick={() => !isLoading && setSelectedStatus("completed")}
                className={`flex items-start gap-3.5 p-3.5 rounded-xl border cursor-pointer transition-all ${
                  selectedStatus === "completed"
                    ? "border-emerald-500 bg-emerald-50/40 ring-2 ring-emerald-500/20 shadow-sm"
                    : "border-gray-200 hover:border-gray-300 hover:bg-gray-50/50"
                } ${isLoading ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <div
                  className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center border transition-colors ${
                    selectedStatus === "completed"
                      ? "border-emerald-600 bg-emerald-600 text-white"
                      : "border-gray-300 bg-white"
                  }`}
                >
                  {selectedStatus === "completed" && (
                    <div className="w-2 h-2 bg-white rounded-full" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span className="text-sm font-semibold text-gray-900">
                      Completed
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Payment has been received and confirmed in full.
                  </p>
                </div>
              </div>

              {/* Pending Option */}
              <div
                onClick={() => !isLoading && setSelectedStatus("pending")}
                className={`flex items-start gap-3.5 p-3.5 rounded-xl border cursor-pointer transition-all ${
                  selectedStatus === "pending"
                    ? "border-amber-500 bg-amber-50/40 ring-2 ring-amber-500/20 shadow-sm"
                    : "border-gray-200 hover:border-gray-300 hover:bg-gray-50/50"
                } ${isLoading ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <div
                  className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center border transition-colors ${
                    selectedStatus === "pending"
                      ? "border-amber-600 bg-amber-600 text-white"
                      : "border-gray-300 bg-white"
                  }`}
                >
                  {selectedStatus === "pending" && (
                    <div className="w-2 h-2 bg-white rounded-full" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-semibold text-gray-900">
                      Pending
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Payment is pending, deferred, or awaiting settlement.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-xs font-medium text-white bg-[#199C4D] hover:bg-[#15803d] rounded-lg shadow-sm transition-colors flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Updating...</span>
                </>
              ) : (
                <span>Update Status</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdatePaymentStatusModal;
